import { Button } from '@cokitchen/cokitchen-components';
import { ReactComponent as ExploreEmptyState } from 'assets/icons/explore-empty-state.svg';
import PageLoader from 'components/base/page-loader';
import ExploreCard from 'components/explore-card';
import SelectContentModal from 'components/modals/select-content-modal';
import useDashboardContext from 'contexts/dashboard.context';
import { useExploreContext } from 'contexts/explore.context';
import { initialExplorePostData } from 'contexts/explore.context/context';
import useAxios from 'hooks/useAxios';
import { ExplorePostType, QueryExplorePostTypeRes } from 'models/explore-posts.model';
import React, { useEffect, useMemo, useState } from 'react';

const ExplorePage = () => {
  const [openCreateContentModal, setOpenCreateContentModal] = useState(false);
  const [explorePostsData, setExplorePostsData] = useState<ExplorePostType[]>([]);
  const { activeCokitchen } = useDashboardContext();
  const { setFormState, setContentUrl } = useExploreContext();

  const { fetchData, loading, response } = useAxios<QueryExplorePostTypeRes>({
    url: `internal/marketing/explore-posts`,
    isInternal: true
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    setExplorePostsData(response?.data ?? []);
  }, [response]);

  const handleDelete = (id: string) =>
    setExplorePostsData(explorePostsData.filter((p) => p.id !== id));

  const resetState = () => {
    setFormState(initialExplorePostData);
    setContentUrl([]);
  };

  const postsToShow = useMemo(() => {
    return explorePostsData.filter((p) => p.cokitchen_id === activeCokitchen.id);
  }, [explorePostsData, activeCokitchen]);

  return (
    <section className='px-10 py-7'>
      <div className='flex items-center justify-between'>
        <div>
          <h2 className='text-gray-6 font-semibold text-2xl tracking-tight mb-3'>Explore</h2>
          <p className='text-gray-8 text-sm'>Create & upload new posts here</p>
        </div>

        {postsToShow?.length > 0 && (
          <Button
            onClick={() => {
              setOpenCreateContentModal(true);
              resetState();
            }}
            color='primary'
            variant='block'
            className='w-32 h-[41px] text-white'>
            Upload post
          </Button>
        )}
      </div>

      {loading ? (
        <PageLoader />
      ) : postsToShow?.length > 0 ? (
        <div className='grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 gap-y-7 mt-10'>
          {postsToShow?.map((post) => (
            <ExploreCard
              key={post?.id}
              exploreData={post}
              handleDelete={() => handleDelete(post.id)}
            />
          ))}
        </div>
      ) : (
        <div className='flex flex-col items-center justify-center min-h-[30rem]'>
          <ExploreEmptyState />
          <h5 className='text-lg font-bold mt-2'>Nothing to see yet</h5>
          <p className='text-center text-sm font-medium text-gray-8/70 max-w-sm mt-1 mb-5'>
            Lorem ipsum dolor sit amet consectetur. Volutpat pulvinar in amet risus at auctor. Sit
            vitae consequat hac neque.
          </p>

          <Button
            onClick={() => {
              resetState();
              setOpenCreateContentModal(true);
            }}
            color='primary'
            variant='block'
            className='py-2 w-28 text-sm font-medium'>
            Upload post
          </Button>
        </div>
      )}

      {openCreateContentModal && (
        <SelectContentModal
          isOpen={openCreateContentModal}
          onClose={() => setOpenCreateContentModal(false)}
        />
      )}
    </section>
  );
};

export default ExplorePage;
